import type { ShopifyProduct } from "@/lib/shopify";
// The opening-variant rule lives in scripts/variant-pick.mjs — ONE
// implementation, shared with the build (scripts/fetch-products.mjs), so the
// price in the prerendered head, JSON-LD and feeds is the price the page shows.
import {
  inStockPriceRange,
  openingImageUrl,
  pickDefaultVariant,
  sameImageUrl,
  titleDescribesVariant,
  variantsDifferInLook as differInLook,
} from "../../scripts/variant-pick.mjs";

// One rule for "the same photo" too: the build compares the opening photo with
// the lead photo the same way (openingImageUrl).
export { sameImageUrl };

type Variant = ShopifyProduct["node"]["variants"]["edges"][number]["node"];

// The build reads each product's first 20 variants (VARIANT_WINDOW in
// scripts/fetch-products.mjs). The product page reads more, so that every
// option has a button; the opening variant is still picked from the same 20,
// or the page could open on a price the prerendered head does not carry.
const VARIANT_WINDOW = 20;
const pickable = (node: ShopifyProduct["node"]) => (node.variants?.edges || []).slice(0, VARIANT_WINDOW).map((e) => e.node);

/**
 * The variant a product page opens on: the variant the title names ("8' X 10'
 * … Rug", "… King Bed"), else the cheapest one in stock. Opening on the
 * cheapest regardless put a 2' X 3' price beside an 8' X 10' name. When the
 * titled variant is sold out this is that variant, sold out.
 */
export function defaultVariant(node: ShopifyProduct["node"]): Variant | undefined {
  return pickDefaultVariant(node.title, pickable(node));
}

/**
 * The photo the page opens on when it is not the lead photo: the opening
 * variant's own, for a product whose variants differ in look. Undefined when
 * the page opens on its lead photo. The head preloads the same file.
 */
export function openingPhoto(node: ShopifyProduct["node"]): Variant["image"] | undefined {
  const lead = node.images?.edges?.[0]?.node.url;
  const url = openingImageUrl(node.title, pickable(node), lead);
  return url && url !== lead ? defaultVariant(node)?.image : undefined;
}

/**
 * Whether the supplier's title and measurements describe `variant`. The
 * supplier writes one set for the size the title names, so the size facts on
 * the page are hidden while another size is selected.
 */
export function sizeFactsApply(node: ShopifyProduct["node"], variant?: Variant): boolean {
  return titleDescribesVariant(node.title, (node.variants?.edges || []).map((e) => e.node), variant);
}

type Money = { amount: string; currencyCode: string };

/**
 * The price a card or list row shows: the lowest in-stock variant price, with
 * `from` set when other in-stock variants cost more ("From $X"). A row with no
 * variant data (search suggestions) falls back to the product's price range.
 */
export function listingPrice(node: {
  title?: string;
  priceRange?: { minVariantPrice?: Money; maxVariantPrice?: Money };
  compareAtPriceRange?: { minVariantPrice?: Money };
  variants?: ShopifyProduct["node"]["variants"];
}): { price?: Money; compareAtPrice?: Money | null; from: boolean } {
  const min = node.priceRange?.minVariantPrice;
  const variants = (node.variants?.edges || []).map((e) => e.node);
  const range = inStockPriceRange(variants);
  if (!range) {
    const max = node.priceRange?.maxVariantPrice;
    const from = !variants.length && !!min && !!max && parseFloat(max.amount) > parseFloat(min.amount);
    return { price: min, compareAtPrice: node.compareAtPriceRange?.minVariantPrice, from };
  }
  const lowest = variants.find((v) => v.availableForSale && parseFloat(v.price.amount) === range.low)!;
  // The variant the title names is sold out: the lowest price is another
  // variant's, so it reads "From" even when the in-stock prices are one.
  const titledSoldOut = node.title != null && pickDefaultVariant(node.title, variants.slice(0, VARIANT_WINDOW))?.availableForSale === false;
  return { price: lowest.price, compareAtPrice: lowest.compareAtPrice, from: range.high > range.low || titledSoldOut };
}

/**
 * The photo for a cart line: the chosen variant's own when the product has
 * variants to choose between, else the product's lead image.
 */
export function lineImageUrl(node: ShopifyProduct["node"], variantId: string): string | undefined {
  const edges = node.variants?.edges || [];
  const own = edges.length > 1 ? edges.find((e) => e.node.id === variantId)?.node.image?.url : undefined;
  return own ?? node.images?.edges?.[0]?.node.url;
}

/**
 * The variant offered where there is no picker (cart suggestions, bundles):
 * the one the product page opens on, so the price beside "8' X 10' … Rug" is
 * the 8' X 10' that goes in the cart, not the first size in stock. Undefined
 * when nothing can be bought.
 */
export function firstAvailableVariant(node: ShopifyProduct["node"]): Variant | undefined {
  const variant = defaultVariant(node);
  return variant?.availableForSale ? variant : undefined;
}

/**
 * Which size or colour a price is for, where a name alone does not say:
 * "Walnut · Queen". A bare number keeps its option's name ("Number of Drawers:
 * 5"). Empty when the product has nothing to choose between.
 */
export function variantLabel(node: ShopifyProduct["node"], variant?: Variant): string {
  if (!variant || (node.variants?.edges?.length ?? 0) < 2) return "";
  return (variant.selectedOptions || [])
    .filter((o) => o.value !== "Default Title")
    .map((o) => (/^[\d.]+$/.test(o.value) ? `${o.name}: ${o.value}` : o.value))
    .join(" · ");
}

const optionValue = (v: Variant, name: string) => v.selectedOptions?.find((o) => o.name === name)?.value;

/**
 * Whether the variants differ in more than size: a colour, a finish, a number
 * of drawers (scripts/variant-pick.mjs, shared with the build).
 */
export function variantsDifferInLook(node: ShopifyProduct["node"]): boolean {
  return differInLook((node.variants?.edges || []).map((e) => e.node));
}

/**
 * The variant to switch to when a shopper picks `value` for option `name`.
 * Keeps the other options as they are when that combination is in stock;
 * otherwise moves to an in-stock variant with the picked value, so a choice
 * never strands the shopper on a sold-out combination while one is available.
 */
export function variantForOption(variants: Variant[], current: Variant | undefined, name: string, value: string): Variant | undefined {
  const withValue = variants.filter((v) => optionValue(v, name) === value);
  const exact = withValue.find((v) =>
    (v.selectedOptions || []).every((o) => o.name === name || o.value === (current && optionValue(current, o.name))),
  );
  if (exact?.availableForSale) return exact;
  return withValue.find((v) => v.availableForSale) ?? exact ?? withValue[0];
}

/** Whether any in-stock variant carries this option value. */
export function optionValueAvailable(variants: Variant[], name: string, value: string): boolean {
  return variants.some((v) => v.availableForSale && optionValue(v, name) === value);
}
